import type { CanonicalUnitLocation, ReservationSetting, Unit } from '../models/data';

export const DEFAULT_UNIT_TIMEZONE = 'Europe/Budapest';
const SUPPORTED_LOCATION_PROVIDERS = new Set(['geoapify', 'open-meteo']);

const trimmedString = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value.trim() : null;

export const isValidIanaTimezone = (value: unknown): value is string => {
  const timezone = trimmedString(value);
  if (!timezone || timezone.length > 100) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: timezone }).format(new Date(0));
    return true;
  } catch {
    return false;
  }
};

export const resolveCanonicalUnitLocation = (
  unit: Pick<Unit, 'canonicalLocation'> | null | undefined,
): CanonicalUnitLocation | null => {
  const location = unit?.canonicalLocation;
  if (!location || location.schemaVersion !== 1 || !SUPPORTED_LOCATION_PROVIDERS.has(location.provider)) return null;
  if (!isValidIanaTimezone(location.timezone)) return null;
  if (!Number.isFinite(location.latitude) || location.latitude < -90 || location.latitude > 90) return null;
  if (!Number.isFinite(location.longitude) || location.longitude < -180 || location.longitude > 180) return null;
  return location;
};

export const resolveUnitTimezone = (
  unit?: Pick<Unit, 'canonicalLocation' | 'timezone' | 'timeZone'> | null,
  reservationSettings?: Pick<ReservationSetting, 'timezone' | 'timeZone'> | null,
  fallback = DEFAULT_UNIT_TIMEZONE,
): string => {
  const candidates = [
    unit?.canonicalLocation?.timezone,
    unit?.timezone,
    unit?.timeZone,
    reservationSettings?.timezone,
    reservationSettings?.timeZone,
    fallback,
  ];
  return candidates.find(isValidIanaTimezone) ?? 'UTC';
};

export const formatUnitLocationSummary = (
  location: CanonicalUnitLocation | null | undefined,
): string => trimmedString(location?.displayName) ?? trimmedString(location?.name) ?? '';
